'use client';

import { Box, Theme } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement } from 'react';

export type StatusKnockoutTileProps = Readonly<{
  /** A single icon element, sized by the caller (the board uses size 40 on both 1c/1c wa and 1d). */
  icon: ReactElement;
  'data-testid'?: string;
}>;

/**
 * The board's status tile: a radial-gradient white knockout behind the icon, not a filled box.
 * `StatusIconTile` (packages/components/src/StatusIconTile) is a DIFFERENT, deliberately solid-fill
 * component correct for ErrorBoundaryScreen and ReportDownloadPage; do not reach for it here, and
 * do not add a `shape`/`background` prop to this component to make it do both jobs. Decision C4/C5
 * settled this exact confusion: `loader.css`'s own opening comment records that reaching for the
 * wrong tile shape shipped "a pink disc" for three revisions before this ruling.
 *
 * The 58.9/59 percent stop sizes the white ground to the glyph disc itself, and the arithmetic is
 * NOT 10/24 of the tile's width. `radial-gradient(circle at center, ...)` with no explicit size
 * keyword defaults to `farthest-corner` (CSS Images spec), so a percentage stop is a fraction of
 * the centre-to-CORNER distance, `(width/2) * sqrt(2)` = 0.70711 x width, not of the width. The
 * glyph disc in `ErrorFilled` and `InfoFilled` (identical `<path>` in both) spans 20 of its 24
 * units, a radius of 10/24 = 0.41667 x width, so as a fraction of the farthest-corner reference
 * that is `(10/24) / (sqrt(2)/2)` = 58.93%.
 *
 * This value went through three passes and the earlier two are worth recording, because each was
 * right about something the next one had to keep.
 *
 * The original 41.6/41.7 read 10/24 as a fraction of the WIDTH. That is the right glyph geometry
 * applied to the wrong radius. It rendered correctly anyway, which is why it survived two reviews.
 *
 * A second pass kept 41.6 on the grounds that the failure is one-sided: too much white shows a
 * ring outside the disc, too little shows nothing at all while the glyph's own opaque fill covers
 * the gap, so a smaller stop sits further from the only visible failure. That is true, and it is
 * also exactly why 41.6 is fragile rather than safe. It clears THIS glyph's knockout, which
 * extends about 0.208 x width from centre, by coincidence. Size the ground to the disc and it is
 * correct for any knockout that fits inside the disc; size it to a number that happens to clear
 * one particular glyph and it is correct until the glyph changes.
 *
 * Rendered at 4x over the real MUI glyph and at the real 40px caller size, 41.6 and 58.9 are
 * pixel-identical, and 75% shows the ring. So 58.9 costs nothing visible and is correct by
 * construction rather than by coincidence.
 *
 * Colour is always the permanent danger token: both of the board's failure states (1c/1c wa's
 * ErrorFilled and 1d's InfoFilled) render in the SAME red, deliberately ("giving 'we don't
 * recognise this link' a grey treatment invented a severity the design does not have", C4). This
 * component therefore takes no colour prop: every caller in this codebase renders the same red.
 */
function StatusKnockoutTileComponent({
  icon,
  'data-testid': dataTestId,
}: StatusKnockoutTileProps): ReactElement {
  return (
    <Box
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        display: 'inline-flex',
        alignSelf: 'center',
        width: 'auto',
        height: 'auto',
        minWidth: 0,
        flex: '0 0 auto',
        border: 0,
        borderRadius: theme.semantic.borderRadius.round,
        overflow: 'hidden',
        lineHeight: 0,
        padding: 0,
        background: `radial-gradient(circle at center, ${theme.palette.common.white} 0 58.9%, transparent 59%)`,
        color: theme.palette.permanent.danger,
        '& > svg': { display: 'block', color: 'inherit' },
      })}
    >
      {icon}
    </Box>
  );
}

export const StatusKnockoutTile = withClarityTheme(StatusKnockoutTileComponent);
